<template>
  <article v-if="!removed" class="m-post" @click="openDetail">
    <!-- 作者信息：头像 + 昵称 +（学校 · 时间） -->
    <header class="m-post__head">
      <!--
        这里原来写死 loading="lazy"，但列表容器在骨架屏期间是 display:none，
        浏览器对隐藏容器里的 lazy 图片不发请求 —— 首屏头像要等骨架屏收起后才开始下载。
        现在交给 <smart-img>：按 index 判断是否首屏，首屏 eager + high 立刻下。
      -->
      <smart-img
        class="m-post__avatar"
        mode="avatar"
        :src="author.avatar"
        :alt="author.nickname"
        :eager="isAboveFold"
        width="38"
        height="38"
        @click.native.stop="handleUserClick"
      />
      <div class="m-post__ident" @click.stop="handleUserClick">
        <div class="m-post__name-row">
          <span class="m-post__nickname">{{ author.nickname }}</span>
          <span class="m-post__anonymous" v-if="!notAnonymous">
            <svg-icon icon-class="nickred" size="15" />
          </span>
        </div>
        <div class="m-post__sub" v-if="subText">
          {{ subTextWithoutVerified }}
          <!-- 匿名身份不展示学校认证角标：角标是实名背书，挂在匿名马甲上等于泄露身份 -->
          <verified-badge v-if="notAnonymous && authorSchoolName" :verified="item.authorSchoolVerified" :size="13" />
        </div>
      </div>
    </header>

    <!-- 正文：标题（可能没有）+ 内容 -->
    <h3 class="m-post__title" v-if="item.title">{{ item.title }}</h3>
    <div class="m-post__body" v-if="item.content">
      <p class="m-post__text">{{ item.content }}</p>
      <span class="m-post__expand" v-if="mayOverflow">全文</span>
    </div>

    <!-- 视频帖：封面 + 播放图标 + 时长，点开进沉浸式播放器 -->
    <VideoCoverBlock
      v-if="isVideo"
      :article="item"
      variant="mobile"
      :eager="isAboveFold"
      @click.native.stop="openDetail"
    />

    <!-- 配图：1 张大图，2/4 张两列，其余三列九宫格 -->
    <div
      class="m-post__images"
      :class="'is-col-' + gridColumns"
      v-else-if="displayImages.length"
      @click.stop
    >
      <div
        class="m-post__image"
        v-for="(img, imgIndex) in displayImages"
        :key="imgIndex"
      >
        <!-- 显示缩略图、预览用原图；单图比九宫格大，所以按列数给不同档位。
             同桌面端卡片：不能加 :lazy，骨架屏期间容器是 display:none，
             el-image 的 isInContainer 判定会失败且不再重试，首屏配图会空着 -->
        <el-image
          :src="contentThumb(img, gridColumns === 1 ? 720 : 360)"
          fit="cover"
          :preview-src-list="item.avatarList"
          @error="onContentThumbError(img)"
        />
        <span
          class="m-post__image-more"
          v-if="restImageCount > 0 && imgIndex === displayImages.length - 1"
          @click.stop="openDetail"
        >
          +{{ restImageCount }}
        </span>
      </div>
    </div>

    <!-- 投票：图片之后、操作栏之前，与桌面卡片和详情页保持同一版式。
         PollView 内部已 @click.stop，点选项不会冒泡到卡片的「打开详情」 -->
    <PollView
      v-if="item.poll"
      :poll="item.poll"
      :article-id="item.id"
      @voted="onPollVoted"
    />

    <!-- 操作栏：点赞 / 评论 / 收藏，与桌面端信息一致 -->
    <footer class="m-post__actions">
      <span
        class="m-post__action"
        :class="{ 'is-liked': item.isLike }"
        @click.stop="handleLike"
      >
        <svg-icon :icon-class="item.isLike ? 'hearted' : 'heart'" size="17" />
        <span class="m-post__action-text">{{ likeText }}</span>
      </span>
      <span class="m-post__action" @click.stop="openDetail">
        <svg-icon icon-class="pingluncount" size="17" />
        <span class="m-post__action-text">{{ commentText }}</span>
      </span>
      <span
        class="m-post__action"
        :class="{ 'is-collected': item.isCollect }"
        @click.stop="handleCollectClick"
      >
        <svg-icon :icon-class="item.isCollect ? 'starred' : 'star'" size="17" />
        <span class="m-post__action-text">{{ collectText }}</span>
      </span>
    </footer>
  </article>
</template>

<script>
import articleMixinx from '@/mixins/articleMixinx'
import aboveFoldMixin from '@/mixins/aboveFoldMixin'
import contentImageMixin from '@/mixins/contentImageMixin'
import { formatFeedTime } from '@/utils/timeFormat'
import PollView from '@/components/poll/PollView.vue'
import VideoCoverBlock from '@/components/video/VideoCoverBlock.vue'
import { isVideoArticle } from '@/utils/articleMedia'

// 九宫格最多展示 9 张（发布端上限也是 9），超出用 +N 提示
const MAX_GRID_IMAGES = 9
// 正文超过这个字数时给一个「全文」提示（无法精确测量行数，用字数近似）
const EXPAND_HINT_LENGTH = 90

export default {
  name: 'MobileFeedItem',
  components: { PollView, VideoCoverBlock },
  mixins: [articleMixinx, aboveFoldMixin, contentImageMixin],
  props: {
    item: {
      type: Object,
      default: () => ({})
    },
    // 在列表中的下标：aboveFoldMixin 用它判断头像是否属于首屏（首屏不懒加载）
    index: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      // 在视频播放器里被作者删掉了：列表数据归父页面管，卡片自己先藏起来
      removed: false
    }
  },
  mounted () {
    // 与桌面端卡片共用同一套同步事件，详情弹窗里的操作会回写到列表
    this.$root.$on('article-data-sync', this.handleArticleSync)
    this.$root.$on('article-deleted', this.handleArticleDeleted)
  },
  beforeDestroy () {
    this.$root.$off('article-data-sync', this.handleArticleSync)
    this.$root.$off('article-deleted', this.handleArticleDeleted)
  },
  computed: {
    author () {
      return this.item.userInfoVO || {}
    },
    isVideo () {
      return isVideoArticle(this.item)
    },
    // 匿名用户不允许跳转个人主页
    notAnonymous () {
      return this.author.nickType === this.$enumType.isAnonymous.NO
    },
    // 发帖时的学校快照。不读 author.school（那是作者当前的资料学校，会随他改教育经历漂移），
    // 老帖子没快照时服务端已经回退成当前学校，这里直接消费结论。
    authorSchoolName () {
      return this.item.authorSchoolName || ''
    },
    // 副标题：学校 · 时间（匿名帖不展示学校，认证图标单独渲染）
    subText () {
      const parts = []
      if (this.notAnonymous && this.authorSchoolName) parts.push(this.authorSchoolName)
      const time = formatFeedTime(this.item.createTime)
      if (time) parts.push(time)
      return parts.join(' · ')
    },
    // 副标题（不含认证图标，用于文本显示）
    subTextWithoutVerified () {
      return this.subText
    },
    // 视频帖的 avatarList 里只有一张封面，不当成配图
    imageList () {
      return this.isVideo ? [] : (this.item.avatarList || [])
    },
    displayImages () {
      return this.imageList.slice(0, MAX_GRID_IMAGES)
    },
    restImageCount () {
      return Math.max(this.imageList.length - MAX_GRID_IMAGES, 0)
    },
    // 1 张单图、2/4 张两列（避免 4 张出现 3+1 的孤图）、其余三列
    gridColumns () {
      const count = this.displayImages.length
      if (count === 1) return 1
      if (count === 2 || count === 4) return 2
      return 3
    },
    mayOverflow () {
      return (this.item.content || '').length > EXPAND_HINT_LENGTH
    },
    likeText () {
      return this.item.likeCount > 0 ? this.formatViews(this.item.likeCount) : '赞'
    },
    commentText () {
      return this.item.commentCount > 0 ? this.formatViews(this.item.commentCount) : '评论'
    },
    collectText () {
      return this.item.collectCount > 0 ? this.formatViews(this.item.collectCount) : '收藏'
    }
  },
  methods: {
    handleArticleSync (data) {
      if (data.id !== this.item.id) return
      // 逐字段判空再写：投票同步只带 { id, poll }，
      // 无条件赋值会把 likeCount / isLike 等一并写成 undefined
      const fields = ['likeCount', 'isLike', 'collectCount', 'isCollect', 'commentCount', 'poll']
      fields.forEach(field => {
        if (data[field] !== undefined) {
          this.$set(this.item, field, data[field])
        }
      })
    },
    /**
     * 投票成功：写回本条数据并广播给详情弹窗、桌面端卡片。
     * poll 是服务端算好的一整份（票数/占比/是否已投），整体替换而不是本地加减。
     */
    onPollVoted (poll) {
      if (!poll) return
      this.$set(this.item, 'poll', poll)
      this.$root.$emit('article-data-sync', { id: this.item.id, poll })
    },
    handleArticleDeleted (data) {
      if (data && String(data.id) === String(this.item.id)) this.removed = true
    },
    // 带上整条数据：视频帖由弹窗直接转给视频播放器，首帧就有封面和文案
    openDetail () {
      this.$root.$articleDialog && this.$root.$articleDialog.openDialog(this.item.id, '', this.item)
    },
    handleLike () {
      this.like(this.item)
    },
    handleCollectClick () {
      this.handleCollect(this.item)
    },
    handleUserClick () {
      if (!this.notAnonymous || !this.author.id) return
      // 移动端不开新标签页，直接站内跳转，符合手机上的返回习惯
      this.$router.push(`/user/${this.author.id}`)
    }
  }
}
</script>

<style scoped lang="scss">
// 移动端信息流卡片：与桌面端列表同构（作者 → 正文 → 配图 → 操作），
// 只是把尺寸、间距、触摸热区按手机重新排了一遍。
.m-post {
  position: relative;
  display: block;
  padding: 14px 12px 4px;
  margin-bottom: 8px;
  background: var(--background-color, #fff);
  border-radius: 12px;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease;
  contain: content;

  &:active {
    background-color: #fafafa;
  }

  // ---------- 作者 ----------
  &__head {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }

  &__avatar {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    margin-right: 10px;
    border-radius: 50%;
    object-fit: cover;
    background: #f2f3f5;
  }

  &__ident {
    min-width: 0;
    flex: 1;
  }

  &__name-row {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  &__nickname {
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    color: #1a1a1a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__anonymous {
    display: inline-flex;
    align-items: center;
    margin-left: 4px;
    flex-shrink: 0;
  }

  &__sub {
    margin-top: 2px;
    font-size: 12px;
    line-height: 16px;
    color: #9a9a9a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 2px;
  }

  // ---------- 正文 ----------
  &__title {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 600;
    line-height: 23px;
    color: #1a1a1a;
    word-break: break-word;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  &__body {
    position: relative;
  }

  &__text {
    margin: 0;
    font-size: 15px;
    line-height: 24px;
    color: #333;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    overflow: hidden;
  }

  // 「全文」压在截断处的行尾（不额外占一行），左侧用渐变把文字淡出。
  // 它不做真实展开，点整卡进详情，和主流社区一致。
  &__expand {
    position: absolute;
    right: 0;
    bottom: 0;
    padding-left: 28px;
    font-size: 15px;
    line-height: 24px;
    color: var(--theme-color, #3a8ee6);
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      var(--background-color, #fff) 55%
    );
  }

  // ---------- 配图 ----------
  &__images {
    display: grid;
    gap: 4px;
    margin-top: 10px;

    &.is-col-1 {
      grid-template-columns: minmax(0, 62%);
    }

    &.is-col-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &.is-col-3 {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  &__image {
    position: relative;
    width: 100%;
    // 用 padding-top 撑高度：比 aspect-ratio 兼容更多老版本微信内核
    padding-top: 100%;
    border-radius: 6px;
    overflow: hidden;
    background: #f2f3f5;

    ::v-deep .el-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  &__image-more {
    position: absolute;
    right: 4px;
    bottom: 4px;
    height: 20px;
    padding: 0 8px;
    line-height: 20px;
    font-size: 11px;
    color: #fff;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 10px;
  }

  // ---------- 操作栏 ----------
  &__actions {
    display: flex;
    align-items: center;
    margin-top: 4px;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    // 单个操作占 1/3 宽度：热区足够大，图标位置在每张卡片上都对齐
    flex: 0 0 33.33%;
    padding: 10px 0;
    // svg 图标内置 fill: #444，文字取相近灰度，避免图标比数字明显更深
    color: #666;

    &:active {
      opacity: 0.6;
    }

    // 图标本身会换成已点赞/已收藏的彩色版本，这里只同步数字颜色
    &.is-liked {
      color: #ff4757;
    }

    &.is-collected {
      color: #e6a23c;
    }
  }

  &__action-text {
    margin-left: 5px;
    font-size: 13px;
    line-height: 1;
    color: inherit;
  }
}
</style>
